import { Component } from '../../scripts/component';
import { fetchConfig, preloadImage } from '../../scripts/utils';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from './events';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    const didError = event.detail.data.didError || false;
    if (!didError) {
      this.animateAddToCart();
    }
  };

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Toggles the loading spinner on the add to cart button.
   * @param {boolean} loading
   */
  setLoading(loading) {
    this.refs.addToCartButton.classList.toggle('is-loading', loading);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    addToCartButton.classList.remove('is-loading');

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** True while the duplicate-size review is in flight. */
  #reviewing = false; 

  productVariantMedia = null;

  connectedCallback() {
    super.connectedCallback();

    this.productVariantMedia = this.dataset.productVariantMedia;

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;
    if (addToCartButtonContainer?.refs.addToCartButton?.classList.contains('is-loading')) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    // Let the duplicate-size modal vet the add first, so a second size is never
    // put in the cart before the customer has chosen what to do about it. The
    // guard stops a double-click opening two reviews during the lookup.
    if (this.#reviewing) return;
    this.#reviewing = true;
    addToCartButtonContainer?.setLoading(true);

    let shouldProceed = false;
    try {
      shouldProceed = await this.#reviewDuplicateSize(formData);
    } catch (error) {
      console.error('[product-form] duplicate size review failed:', error);
      shouldProceed = false;
    } finally {
      this.#reviewing = false;
      if (!shouldProceed) {
        addToCartButtonContainer?.setLoading(false);
      }
    }

    if (!shouldProceed) return;

    const fetchCfg = fetchConfig('javascript', { body: formData });

    // make sure variant image is already preloaded before the fetch
    this.#preloadImage();

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        addToCartButtonContainer?.setLoading(false);

        if (response.status) {
          this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        addToCartButtonContainer?.setLoading(false);
        console.error(error);
      });
  }

  /**
   * Asks the duplicate-size modal whether this add-to-cart should go ahead.
   * Always fails open so a missing or broken modal can never block a sale.
   *
   * @param {FormData} formData
   * @returns {Promise<boolean>}
   */
  async #reviewDuplicateSize(formData) {
    try {
      const modal = /** @type {any} */ (document.querySelector('duplicate-size-modal'));
      if (typeof modal?.reviewAdd !== 'function') return true;

      return await modal.reviewAdd({
        productId: this.dataset.productId,
        variantId: String(formData.get('id') || ''),
      });
    } catch (error) {
      console.error('[product-form] duplicate size check failed:', error);
      return true;
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  #preloadImage = () => {
    if (!this.productVariantMedia) return;
    preloadImage(this.productVariantMedia);
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    // Skip if this update is for a different product
    if (event.detail.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data?.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (!currentAddToCartButton) return;

    // Update the button state
    const isAvailable = variant.available !== false;
    if (isAvailable) {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');

      // Update button text
      const buttonTextElement = currentAddToCartButton.querySelector('.add-to-cart-text');
      if (buttonTextElement && Theme.translations.addToCartText) {
        buttonTextElement.textContent = Theme.translations.addToCartText;
      }
    } else {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');

      // Update button text
      const buttonTextElement = currentAddToCartButton.querySelector('.add-to-cart-text');
      if (buttonTextElement && Theme.translations.outOfStock) {
        buttonTextElement.textContent = Theme.translations.outOfStock;
      }
    }

    // Update the variant ID
    variantId.value = variant.id || '';

    // Update any variant-specific UI elements
    this.updateVariantSpecificUI(variant);
  };

  /**
   * Updates variant-specific UI elements
   * @param {Object} variant - The variant data
   */
  updateVariantSpecificUI(variant) {
    // Update quantity selector max
    const quantityInput = this.querySelector('input[type="number"][name="quantity"]');
    if (quantityInput) {
      const maxQuantity = variant.inventory_quantity > 0 ? variant.inventory_quantity : 10;
      quantityInput.max = Math.max(1, maxQuantity);

      // Reset quantity to 1 if current value exceeds max
      if (parseInt(quantityInput.value, 10) > maxQuantity) {
        quantityInput.value = '1';
      }
    }

    // Update any other variant-specific UI elements here
    // For example, you might want to update a "Low Stock" indicator
    const lowStockIndicator = this.querySelector('[data-low-stock]');
    if (lowStockIndicator) {
      const isLowStock = variant.inventory_quantity > 0 && variant.inventory_quantity <= (window.themeVariables?.settings?.lowStockThreshold || 5);
      lowStockIndicator.style.display = isLowStock ? 'inline' : 'none';
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
